/**
 * useI18n Hook
 * Custom hook for internationalization features
 */

import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import {
  SupportedLanguage,
  SupportedRegion,
  RegionalCustomization,
  LANGUAGE_REGION_MAP,
} from '../../../shared/interfaces/i18n.interface';
import { changeLanguage as changeI18nLanguage } from '../i18n/config';

interface UseI18nReturn {
  // Translation function
  t: (key: string, options?: any) => string;

  // Current language and region
  language: SupportedLanguage;
  region: SupportedRegion;

  // Language management
  changeLanguage: (language: SupportedLanguage) => Promise<void>;
  changeRegion: (region: SupportedRegion) => Promise<void>;

  // Regional settings
  regional: RegionalCustomization | null;

  // Currency formatting
  formatCurrency: (amount: number) => string;

  // Date formatting
  formatDate: (date: Date | string, format?: string) => string;

  // Number formatting
  formatNumber: (value: number) => string;

  // Loading state
  isLoading: boolean;

  // Direction (for RTL support in future)
  direction: 'ltr' | 'rtl';
}

export const useI18n = (): UseI18nReturn => {
  const { t, i18n, ready } = useTranslation();
  const [regional, setRegional] = useState<RegionalCustomization | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get current language and region
  const language = (i18n.language || 'en') as SupportedLanguage;
  const region = LANGUAGE_REGION_MAP[language];

  // Load regional settings
  useEffect(() => {
    const loadRegionalSettings = async () => {
      try {
        // Check localStorage first
        const cached = localStorage.getItem('sigma08_regional');
        if (cached) {
          setRegional(JSON.parse(cached));
          return;
        }

        // Fetch from server
        const response = await fetch(`/api/v1/i18n/regional/${region}`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setRegional(data.data);
            localStorage.setItem('sigma08_regional', JSON.stringify(data.data));
          }
        }
      } catch (error) {
        console.warn('[useI18n] Could not load regional settings:', error);
        // Use default settings
        setRegional(getDefaultRegionalSettings(region));
      }
    };

    loadRegionalSettings();
  }, [region]);

  // Change language
  const changeLanguage = useCallback(async (newLanguage: SupportedLanguage) => {
    setIsLoading(true);
    try {
      await changeI18nLanguage(newLanguage);

      // Update regional settings for new language
      const newRegion = LANGUAGE_REGION_MAP[newLanguage];
      const response = await fetch(`/api/v1/i18n/regional/${newRegion}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setRegional(data.data);
          localStorage.setItem('sigma08_regional', JSON.stringify(data.data));
        }
      }
    } catch (error) {
      console.error('[useI18n] Language change failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Change region (may be different from language in future)
  const changeRegion = useCallback(async (newRegion: SupportedRegion) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/v1/i18n/regional/${newRegion}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setRegional(data.data);
          localStorage.setItem('sigma08_regional', JSON.stringify(data.data));
          localStorage.setItem('sigma08_region', newRegion);
        }
      }
    } catch (error) {
      console.error('[useI18n] Region change failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Format currency
  const formatCurrency = useCallback(
    (amount: number): string => {
      if (!regional) {
        // Fallback formatting based on language
        const localeMap = {
          en: 'en-US',
          'pt-BR': 'pt-BR',
          'pt-PT': 'pt-PT',
          es: 'es-ES',
        };
        const currencyMap = {
          en: 'USD',
          'pt-BR': 'BRL',
          'pt-PT': 'EUR',
          es: 'EUR',
        };

        const locale = localeMap[language] || 'en-US';
        const currency = currencyMap[language] || 'USD';

        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        }).format(amount);
      }

      const { currency } = regional;
      const localeMap = {
        en: 'en-US',
        'pt-BR': 'pt-BR',
        'pt-PT': 'pt-PT',
        es: 'es-ES',
      };

      const locale = localeMap[language] || 'en-US';
      const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: currency.precision,
        maximumFractionDigits: currency.precision,
      }).format(amount);

      return formatted;
    },
    [regional, language]
  );

  // Format date
  const formatDate = useCallback(
    (date: Date | string, format: string = 'long'): string => {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const localeMap = {
        en: 'en-US',
        'pt-BR': 'pt-BR',
        'pt-PT': 'pt-PT',
        es: 'es-ES',
      };

      const locale = localeMap[language] || 'en-US';

      const options: Intl.DateTimeFormatOptions =
        format === 'short'
          ? {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            }
          : format === 'long'
            ? {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }
            : {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              };

      return new Intl.DateTimeFormat(locale, options).format(dateObj);
    },
    [language]
  );

  // Format number
  const formatNumber = useCallback(
    (value: number): string => {
      const localeMap = {
        en: 'en-US',
        'pt-BR': 'pt-BR',
        'pt-PT': 'pt-PT',
        es: 'es-ES',
      };

      const locale = localeMap[language] || 'en-US';
      return new Intl.NumberFormat(locale).format(value);
    },
    [language]
  );

  return {
    t,
    language,
    region,
    changeLanguage,
    changeRegion,
    regional,
    formatCurrency,
    formatDate,
    formatNumber,
    isLoading: isLoading || !ready,
    direction: 'ltr', // Always LTR for en/pt/es
  };
};

// Helper function for default regional settings
function getDefaultRegionalSettings(region: SupportedRegion): RegionalCustomization {
  const defaults: Record<SupportedRegion, RegionalCustomization> = {
    US: {
      region: 'US',
      language: 'en',
      currency: {
        code: 'USD',
        symbol: '$',
        position: 'before',
        decimal: '.',
        thousands: ',',
        precision: 2,
      },
      pricing: {
        currency: 'USD',
        taxRate: 0.08,
        includesTax: false,
      },
      legal: {
        privacyPolicyUrl: '/legal/privacy',
        termsOfServiceUrl: '/legal/terms',
        cookiePolicyUrl: '/legal/cookies',
      },
      support: {
        email: 'support@sigma08.com',
        workingHours: '9 AM - 6 PM EST',
        timezone: 'America/New_York',
      },
    },
    BR: {
      region: 'BR',
      language: 'pt-BR',
      currency: {
        code: 'BRL',
        symbol: 'R$',
        position: 'before',
        decimal: ',',
        thousands: '.',
        precision: 2,
      },
      pricing: {
        currency: 'BRL',
        taxRate: 0.18,
        includesTax: true,
      },
      legal: {
        privacyPolicyUrl: '/legal/privacidade',
        termsOfServiceUrl: '/legal/termos',
        cookiePolicyUrl: '/legal/cookies',
      },
      support: {
        email: 'suporte@sigma08.com.br',
        workingHours: '9h às 18h BRT',
        timezone: 'America/Sao_Paulo',
      },
    },
    PT: {
      region: 'PT',
      language: 'pt-PT',
      currency: {
        code: 'EUR',
        symbol: '€',
        position: 'after',
        decimal: ',',
        thousands: ' ',
        precision: 2,
      },
      pricing: {
        currency: 'EUR',
        taxRate: 0.23,
        includesTax: true,
      },
      legal: {
        privacyPolicyUrl: '/legal/privacidade',
        termsOfServiceUrl: '/legal/termos',
        cookiePolicyUrl: '/legal/cookies',
      },
      support: {
        email: 'suporte@sigma08.pt',
        workingHours: '9h às 18h WET',
        timezone: 'Europe/Lisbon',
      },
    },
    ES: {
      region: 'ES',
      language: 'es',
      currency: {
        code: 'EUR',
        symbol: '€',
        position: 'after',
        decimal: ',',
        thousands: '.',
        precision: 2,
      },
      pricing: {
        currency: 'EUR',
        taxRate: 0.21,
        includesTax: true,
      },
      legal: {
        privacyPolicyUrl: '/legal/privacidad',
        termsOfServiceUrl: '/legal/terminos',
        cookiePolicyUrl: '/legal/cookies',
      },
      support: {
        email: 'soporte@sigma08.es',
        workingHours: '9:00 - 18:00 CET',
        timezone: 'Europe/Madrid',
      },
    },
    MX: {
      region: 'MX',
      language: 'es',
      currency: {
        code: 'MXN',
        symbol: '$',
        position: 'before',
        decimal: '.',
        thousands: ',',
        precision: 2,
      },
      pricing: {
        currency: 'MXN',
        taxRate: 0.16,
        includesTax: false,
      },
      legal: {
        privacyPolicyUrl: '/legal/privacidad',
        termsOfServiceUrl: '/legal/terminos',
        cookiePolicyUrl: '/legal/cookies',
      },
      support: {
        email: 'soporte@sigma08.mx',
        workingHours: '9:00 - 18:00 CST',
        timezone: 'America/Mexico_City',
      },
    },
    AR: {
      region: 'AR',
      language: 'es',
      currency: {
        code: 'ARS',
        symbol: '$',
        position: 'before',
        decimal: ',',
        thousands: '.',
        precision: 2,
      },
      pricing: {
        currency: 'ARS',
        taxRate: 0.21,
        includesTax: true,
      },
      legal: {
        privacyPolicyUrl: '/legal/privacidad',
        termsOfServiceUrl: '/legal/terminos',
        cookiePolicyUrl: '/legal/cookies',
      },
      support: {
        email: 'soporte@sigma08.ar',
        workingHours: '9:00 - 18:00 ART',
        timezone: 'America/Argentina/Buenos_Aires',
      },
    },
  };

  return defaults[region] || defaults.US;
}

export default useI18n;
